import type {
  MetaFunction,
  LoaderFunctionArgs,
  ActionFunctionArgs,
} from '@remix-run/node'
import type { Interval, Plan } from '#app/modules/stripe/plans'
import { useState } from 'react'
import { Form, useLoaderData } from '@remix-run/react'
import { redirect } from '@remix-run/node'
import { requireSessionUser } from '#app/modules/auth/auth.server'
import {
  PLANS,
  PRICING_PLANS,
  PLAN_LIMITS,
  PLAN_ORDER,
  INTERVALS,
  CURRENCIES,
} from '#app/modules/stripe/plans'
import {
  createSubscriptionCheckout,
  createCustomerPortal,
} from '#app/modules/stripe/queries.server'
import { prisma } from '#app/utils/db.server'
import { getLocaleCurrency } from '#app/utils/misc.server'
import { INTENTS } from '#app/utils/constants/misc'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'
import { Switch } from '#app/components/ui/switch'
import { Button } from '#app/components/ui/button'
import { Badge } from '#app/components/ui/badge'
import { track } from '#app/utils/analytics'
import { Check, X } from 'lucide-react'

export const ROUTE_PATH = '/dashboard/settings/billing' as const

export const meta: MetaFunction = () => {
  return [
    { title: 'Inbox Bridge - Billing' },
    { name: 'robots', content: 'noindex, nofollow' },
  ]
}

export async function loader({ request }: LoaderFunctionArgs) {
  const sessionUser = await requireSessionUser(request, {
    redirectTo: LOGIN_PATH,
  })

  const subscription = await prisma.subscription.findUnique({
    where: { userId: sessionUser.id },
    include: { plan: true },
  })
  const currency = getLocaleCurrency(request)

  // Current usage counts
  const bridgeCount = await prisma.bridge.count({
    where: { userId: sessionUser.id },
  })

  const periodStart = subscription?.currentPeriodStart
  const since = periodStart
    ? new Date(periodStart * 1000)
    : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  const monthlyEmailCount = await prisma.inboundEmail.count({
    where: {
      bridge: { userId: sessionUser.id },
      receivedAt: { gte: since },
    },
  })

  return { subscription, currency, bridgeCount, monthlyEmailCount }
}

export async function action({ request }: ActionFunctionArgs) {
  const sessionUser = await requireSessionUser(request, {
    redirectTo: LOGIN_PATH,
  })

  const formData = await request.formData()
  const intent = formData.get(INTENTS.INTENT)

  if (intent === INTENTS.SUBSCRIPTION_CREATE_CHECKOUT) {
    const planId = String(formData.get('planId'))
    const planInterval = String(formData.get('planInterval'))
    const checkoutUrl = await createSubscriptionCheckout({
      userId: sessionUser.id,
      planId,
      planInterval,
      request,
    })
    if (!checkoutUrl) return { success: false }
    return redirect(checkoutUrl)
  }
  if (intent === INTENTS.SUBSCRIPTION_CREATE_CUSTOMER_PORTAL) {
    const customerPortalUrl = await createCustomerPortal({
      userId: sessionUser.id,
    })
    if (!customerPortalUrl) return { success: false }
    return redirect(customerPortalUrl)
  }

  return {}
}

const FEATURE_LABELS: { key: keyof (typeof PLAN_LIMITS)[Plan]; label: string }[] = [
  { key: 'maxBridges', label: 'Bridges' },
  { key: 'maxMonthlyEmails', label: 'Emails / month' },
  { key: 'maxRetryAttempts', label: 'Retry attempts' },
  { key: 'hasManualReplay', label: 'Manual replay' },
  { key: 'hasJsonViewer', label: 'JSON viewer' },
  { key: 'hasAttachments', label: 'Attachment downloads' },
  { key: 'hasPrioritySupport', label: 'Priority support' },
]

function formatLimitValue(_key: string, value: number | boolean): React.ReactNode {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground/40" />
    )
  }
  return value.toLocaleString()
}

export default function DashboardBilling() {
  const { subscription, currency, bridgeCount, monthlyEmailCount } =
    useLoaderData<typeof loader>()

  const currentPlanId = (subscription?.planId as Plan) ?? PLANS.FREE
  const currentInterval = (subscription?.interval as Interval) ?? INTERVALS.MONTH
  const currentLimits = PLAN_LIMITS[currentPlanId]
  const currentPlanIndex = PLAN_ORDER.indexOf(currentPlanId)

  const [selectedPlanId, setSelectedPlanId] = useState<Plan>(currentPlanId)
  const [selectedPlanInterval, setSelectedPlanInterval] = useState<Interval>(
    INTERVALS.MONTH,
  )

  const selectedPlanIndex = PLAN_ORDER.indexOf(selectedPlanId)
  const isUpgrade = selectedPlanIndex > currentPlanIndex
  const isDowngrade = selectedPlanIndex < currentPlanIndex
  const isIntervalChange =
    selectedPlanId === currentPlanId &&
    selectedPlanId !== PLANS.FREE &&
    selectedPlanInterval !== currentInterval
  const isCurrent = selectedPlanId === currentPlanId && !isIntervalChange

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Plans */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full items-center justify-between p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">Plan</h2>
            <p className="flex items-start gap-1 text-sm font-normal text-primary/60">
              You are currently on the{' '}
              <span className="flex h-[18px] items-center rounded-md bg-primary/10 px-1.5 text-sm font-medium text-primary/80">
                {currentPlanId.charAt(0).toUpperCase() + currentPlanId.slice(1)}
              </span>
              plan.
              {subscription && subscription.planId !== PLANS.FREE && (
                <span className="ml-1">
                  {subscription.cancelAtPeriodEnd ? (
                    <span className="text-sm font-medium text-red-500">
                      Expires{' '}
                      {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('en-US')}.
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-green-500">
                      Renews{' '}
                      {new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString('en-US')}.
                    </span>
                  )}
                </span>
              )}
            </p>
          </div>

          {/* Billing interval toggle */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-primary/60">Monthly</label>
            <Switch
              checked={selectedPlanInterval === INTERVALS.YEAR}
              onCheckedChange={() =>
                setSelectedPlanInterval((prev) =>
                  prev === INTERVALS.MONTH ? INTERVALS.YEAR : INTERVALS.MONTH,
                )
              }
            />
            <label className="text-sm text-primary/60">
              Yearly <span className="text-xs text-green-500">(-20%)</span>
            </label>
          </div>
        </div>

        {/* Plan cards */}
        <div className="flex w-full flex-col items-center justify-evenly gap-2 border-border p-6 pt-0">
          {PLAN_ORDER.map((planId) => {
            const plan = PRICING_PLANS[planId]
            const limits = PLAN_LIMITS[planId]
            const isSelected = selectedPlanId === planId
            const isCurrentPlan = currentPlanId === planId
            const price =
              planId === PLANS.FREE
                ? 0
                : selectedPlanInterval === INTERVALS.MONTH
                  ? plan.prices[INTERVALS.MONTH][currency] / 100
                  : plan.prices[INTERVALS.YEAR][currency] / 100

            return (
              <div
                key={planId}
                tabIndex={0}
                role="button"
                className={`flex w-full select-none items-center rounded-md border border-border hover:border-primary/60 ${
                  isSelected && 'border-primary/60 ring-1 ring-primary/60'
                }`}
                onClick={() => setSelectedPlanId(planId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSelectedPlanId(planId)
                }}>
                <div className="flex w-full flex-col items-start p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium text-primary">
                      {plan.name}
                    </span>
                    {isCurrentPlan && (
                      <Badge variant="secondary" className="text-xs">
                        Current
                      </Badge>
                    )}
                    {planId !== PLANS.FREE && (
                      <span className="flex items-center rounded-md bg-primary/10 px-1.5 text-sm font-medium text-primary/80">
                        {currency === CURRENCIES.USD ? '$' : '€'}
                        {price} / {selectedPlanInterval === INTERVALS.MONTH ? 'month' : 'year'}
                      </span>
                    )}
                  </div>
                  <p className="text-start text-sm font-normal text-primary/60">
                    {plan.description}
                  </p>

                  {/* Feature summary when selected */}
                  {isSelected && (
                    <div className="mt-3 grid w-full grid-cols-2 gap-x-8 gap-y-1 border-t pt-3 sm:grid-cols-3">
                      {FEATURE_LABELS.map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-primary/60">{label}</span>
                          <span className="font-medium text-primary">
                            {formatLimitValue(key, limits[key])}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {isUpgrade && 'You will not be charged for testing the subscription upgrade.'}
            {isDowngrade && 'You will be redirected to manage your subscription.'}
            {isIntervalChange &&
              (selectedPlanInterval === INTERVALS.YEAR
                ? 'Switch to annual billing and save 20%.'
                : 'Switch to monthly billing.')}
            {isCurrent && 'Select a different plan to upgrade or downgrade.'}
          </p>
          {isIntervalChange && (
            <Form method="POST">
              <input type="hidden" name="planId" value={selectedPlanId} />
              <input type="hidden" name="planInterval" value={selectedPlanInterval} />
              <Button
                type="submit"
                size="sm"
                name={INTENTS.INTENT}
                value={INTENTS.SUBSCRIPTION_CREATE_CHECKOUT}
                onClick={() =>
                  track('Subscription Interval Change Started', {
                    plan_id: selectedPlanId,
                    from_interval: currentInterval,
                    to_interval: selectedPlanInterval,
                  })
                }>
                Switch to {selectedPlanInterval === INTERVALS.YEAR ? 'annual' : 'monthly'}
              </Button>
            </Form>
          )}
          {isUpgrade && (
            <Form method="POST">
              <input type="hidden" name="planId" value={selectedPlanId} />
              <input type="hidden" name="planInterval" value={selectedPlanInterval} />
              <Button
                type="submit"
                size="sm"
                name={INTENTS.INTENT}
                value={INTENTS.SUBSCRIPTION_CREATE_CHECKOUT}
                onClick={() =>
                  track('Subscription Checkout Started', {
                    plan_id: selectedPlanId,
                    interval: selectedPlanInterval,
                  })
                }>
                Upgrade to {PRICING_PLANS[selectedPlanId].name}
              </Button>
            </Form>
          )}
          {isDowngrade && (
            <Form method="POST">
              <Button
                type="submit"
                size="sm"
                variant="outline"
                name={INTENTS.INTENT}
                value={INTENTS.SUBSCRIPTION_CREATE_CUSTOMER_PORTAL}
                onClick={() => track('Customer Portal Opened')}>
                Manage subscription
              </Button>
            </Form>
          )}
        </div>
      </div>

      {/* Current Usage */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">Usage</h2>
          <p className="text-sm font-normal text-primary/60">
            Current usage on your{' '}
            <span className="font-medium text-primary/80">
              {currentPlanId.charAt(0).toUpperCase() + currentPlanId.slice(1)}
            </span>{' '}
            plan.
          </p>
        </div>

        <div className="flex w-full flex-col gap-4 border-t border-border px-6 py-4">
          {(
            [
              {
                label: 'Bridges',
                used: bridgeCount,
                limit: currentLimits.maxBridges,
              },
              {
                label: 'Emails this month',
                used: monthlyEmailCount,
                limit: currentLimits.maxMonthlyEmails,
              },
              {
                label: 'Retry attempts per email',
                used: null,
                limit: currentLimits.maxRetryAttempts,
              },
            ] as const
          ).map(({ label, used, limit }) => {
            const pct = used !== null ? Math.min((used / limit) * 100, 100) : null
            return (
              <div key={label} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-primary/60">{label}</span>
                  <span className="font-medium text-primary">
                    {used !== null ? (
                      <>
                        {used.toLocaleString()}{' '}
                        <span className="text-primary/40">/ {limit.toLocaleString()}</span>
                      </>
                    ) : (
                      <>{limit.toLocaleString()} max</>
                    )}
                  </span>
                </div>
                {pct !== null && (
                  <div className="h-1.5 w-full rounded-full bg-primary/10">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pct >= 90
                          ? 'bg-red-500'
                          : pct >= 75
                            ? 'bg-yellow-500'
                            : 'bg-primary/60'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Manage Subscription */}
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">Manage Subscription</h2>
          <p className="flex items-start gap-1 text-sm font-normal text-primary/60">
            Update your payment method, billing address, and more.
          </p>
        </div>

        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 py-3 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            You will be redirected to the Stripe Customer Portal.
          </p>
          <Form method="POST">
            <Button
              type="submit"
              size="sm"
              name={INTENTS.INTENT}
              value={INTENTS.SUBSCRIPTION_CREATE_CUSTOMER_PORTAL}
              onClick={() => track('Customer Portal Opened')}>
              Manage
            </Button>
          </Form>
        </div>
      </div>
    </div>
  )
}
